/**
 * Breathing App - Hot Flash Relief Tool
 * Rosebud Woman
 */

(function() {
  'use strict';

  // ==========================================================================
  // Configuration
  // ==========================================================================

  const METHODS = {
    paced: {
      name: 'Paced Breathing',
      phases: [
        { type: 'inhale', duration: 5000, scale: 1.4 },
        { type: 'exhale', duration: 5000, scale: 1.0 }
      ]
    },
    box: {
      name: 'Box Breathing',
      phases: [
        { type: 'inhale', duration: 4000, scale: 1.4 },
        { type: 'hold', duration: 4000, scale: 1.4 },
        { type: 'exhale', duration: 4000, scale: 1.0 },
        { type: 'hold', duration: 4000, scale: 1.0 }
      ]
    },
    sigh: {
      name: 'Physiological Sigh',
      phases: [
        { type: 'inhale', duration: 3000, scale: 1.3 },
        { type: 'inhale-top', duration: 1000, scale: 1.4 }, // Quick second inhale
        { type: 'exhale', duration: 6000, scale: 1.0 }
      ]
    },
    478: {
      name: '4-7-8 Cooling Breath',
      phases: [
        { type: 'inhale', duration: 4000, scale: 1.4 },
        { type: 'hold', duration: 7000, scale: 1.4 },
        { type: 'exhale', duration: 8000, scale: 1.0 }
      ]
    }
  };

  const PROMPTS = [
    'Feel the cool arctic air...',
    'Imagine the chill spreading through your body...',
    'Let the cold wash away the heat...',
    'Cool waves flowing through you...',
    'Embrace the refreshing stillness...'
  ];

  const STORAGE_KEYS = {
    leadCaptured: 'breathingApp_leadCaptured',
    dismissCount: 'breathingApp_dismissCount',
    preferredMethod: 'breathingApp_method',
    soundEnabled: 'breathingApp_sound'
  };

  const LEAD_CAPTURE_THRESHOLD = 12; // breaths before showing modal
  const MAX_DISMISS_COUNT = 3; // stop showing after this many dismissals

  // ==========================================================================
  // State
  // ==========================================================================

  let state = {
    method: 'paced',
    isRunning: false,
    isPaused: false,
    currentPhaseIndex: 0,
    breathCount: 0,
    targetRounds: 0, // 0 = infinite
    animationFrame: null,
    phaseStartTime: null,
    phaseDuration: null,
    currentScale: 1,
    targetScale: 1,
    soundEnabled: false,
    promptIndex: 0,
    hasShownLeadCapture: false,
    countdownInterval: null,
    countdownValue: 0
  };

  // ==========================================================================
  // DOM References
  // ==========================================================================

  let elements = {};

  function initElements() {
    elements = {
      app: document.getElementById('breathingApp'),
      video: document.getElementById('backgroundVideo'),
      circle: document.getElementById('breathingCircle'),
      scrollHint: document.getElementById('scrollHint'),
      backBtn: document.getElementById('backBtn'),
      phaseText: document.getElementById('phaseText'),
      promptText: document.getElementById('promptText'),
      countdown: document.getElementById('countdown'),
      methodLabel: document.getElementById('methodLabel'),
      breathCount: document.getElementById('breathCount'),
      modeBtn: document.getElementById('modeBtn'),
      bottomButtons: document.getElementById('bottomButtons'),
      roundBtns: document.querySelectorAll('[data-rounds]'),
      soundBtn: document.getElementById('soundBtn'),
      methodModal: document.getElementById('methodModal'),
      methodCards: document.querySelectorAll('[data-method]'),
      leadModal: document.getElementById('leadModal'),
      leadForm: document.getElementById('leadForm'),
      emailInput: document.getElementById('emailInput'),
      completedBreaths: document.getElementById('completedBreaths')
    };
  }

  // ==========================================================================
  // Audio (Web Audio API for minimal footprint)
  // ==========================================================================

  let audioContext = null;

  function initAudio() {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.log('Web Audio not supported');
    }
  }

  function playChime(frequency = 440, duration = 0.15) {
    if (!audioContext || !state.soundEnabled) return;

    // Resume context if suspended (iOS requirement)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  }

  // Beautiful singing bowl / sound bath tone
  function playSingingBowl(baseFrequency = 256, isInhale = true) {
    if (!audioContext || !state.soundEnabled) return;

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const now = audioContext.currentTime;
    const duration = 2.5;

    // Frequencies: inhale = higher, exhale = lower
    const freq = isInhale ? baseFrequency * 1.5 : baseFrequency;

    // Create multiple oscillators for rich harmonic content
    const harmonics = [1, 2, 3, 4.5, 6];
    const gains = [0.15, 0.08, 0.04, 0.02, 0.01];

    harmonics.forEach((harmonic, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();

      osc.connect(gain);
      gain.connect(audioContext.destination);

      osc.frequency.value = freq * harmonic;
      osc.type = 'sine';

      // Gentle attack, long decay
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(gains[i], now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      osc.start(now);
      osc.stop(now + duration);
    });
  }

  // ==========================================================================
  // Video Handling
  // ==========================================================================

  let videoDirection = 1; // 1 = forward, -1 = backward
  let lastVideoTime = 0;

  function initVideo() {
    const video = elements.video;
    if (!video) return;

    // Handle responsive video source
    const updateVideoSource = () => {
      const isMobile = window.innerWidth < 768;
      const desktopSrc = video.querySelector('source[data-desktop]');

      if (desktopSrc) {
        // Get mobile video URL from data attribute
        const mobileVideo = elements.app.dataset.videoMobile;
        const desktopVideo = desktopSrc.getAttribute('src');

        if (isMobile && mobileVideo) {
          video.src = mobileVideo;
        } else if (desktopVideo) {
          video.src = desktopVideo;
        }
      }
    };

    // Play video when ready
    video.addEventListener('canplay', () => {
      video.play().catch(() => {
        // Autoplay blocked - that's okay, we have the poster
      });
    });

    // Ping-pong loop: reverse at ends instead of jarring restart
    video.addEventListener('timeupdate', () => {
      const buffer = 0.1;

      if (videoDirection === 1 && video.currentTime >= video.duration - buffer) {
        // Reached end, start going backward
        videoDirection = -1;
        video.pause();
        pingPongVideo();
      } else if (videoDirection === -1 && video.currentTime <= buffer) {
        // Reached start, start going forward
        videoDirection = 1;
        video.play();
      }
    });

    // Handle video errors gracefully
    video.addEventListener('error', () => {
      video.style.display = 'none';
    });

    updateVideoSource();
    window.addEventListener('resize', debounce(updateVideoSource, 250));
  }

  function pingPongVideo() {
    const video = elements.video;
    if (!video || videoDirection !== -1) return;

    // Manual reverse playback since negative playbackRate isn't widely supported
    const step = 1 / 30; // ~30fps

    function reverseStep() {
      if (videoDirection !== -1 || !video) return;

      video.currentTime = Math.max(0, video.currentTime - step);

      if (video.currentTime > 0) {
        requestAnimationFrame(reverseStep);
      } else {
        videoDirection = 1;
        video.play();
      }
    }

    requestAnimationFrame(reverseStep);
  }

  // ==========================================================================
  // Breathing Animation
  // ==========================================================================

  function startBreathing() {
    if (state.isRunning) return;

    state.isRunning = true;
    state.isPaused = false;
    state.currentPhaseIndex = 0;
    state.breathCount = 0;

    // Enter fullscreen mode
    elements.app.classList.add('is-fullscreen');
    elements.app.removeAttribute('data-paused');
    updateBreathCount();
    startPhase();
  }

  function togglePause() {
    if (!state.isRunning) return;

    state.isPaused = !state.isPaused;

    if (state.isPaused) {
      elements.app.setAttribute('data-paused', 'true');
      cancelAnimationFrame(state.animationFrame);
      elements.countdown.classList.remove('visible');
    } else {
      elements.app.removeAttribute('data-paused');
      elements.countdown.classList.add('visible');
      // Restart current phase from where we left off
      state.phaseStartTime = performance.now() - (state.phaseDuration - getRemainingTime());
      animate();
    }
  }

  function getRemainingTime() {
    if (!state.phaseStartTime || !state.phaseDuration) return 0;
    const elapsed = performance.now() - state.phaseStartTime;
    return Math.max(0, state.phaseDuration - elapsed);
  }

  function startPhase() {
    const method = METHODS[state.method];
    const phase = method.phases[state.currentPhaseIndex];

    state.phaseStartTime = performance.now();
    state.phaseDuration = phase.duration;
    state.targetScale = phase.scale;

    // Update UI state
    const phaseType = phase.type === 'inhale-top' ? 'inhale' : phase.type;
    elements.app.setAttribute('data-state', phaseType);

    // Update phase text
    updatePhaseText(phase.type);

    // Start countdown
    startCountdown(Math.ceil(phase.duration / 1000));

    // Play singing bowl sound
    if (phase.type === 'inhale') {
      playSingingBowl(280, true); // Higher tone for inhale
    } else if (phase.type === 'inhale-top') {
      playSingingBowl(340, true); // Even higher for top-up
    } else if (phase.type === 'exhale') {
      playSingingBowl(220, false); // Lower tone for exhale
    } else if (phase.type === 'hold') {
      playSingingBowl(260, true); // Mid tone for hold
    }

    // Show prompt during holds and long exhales
    if (phase.type === 'hold' || (phase.type === 'exhale' && phase.duration >= 6000)) {
      showPrompt();
    } else {
      hidePrompt();
    }

    // Handle top-up animation for physiological sigh
    if (phase.type === 'inhale-top') {
      elements.circle.classList.add('top-up');
      setTimeout(() => elements.circle.classList.remove('top-up'), 300);
    }

    animate();
  }

  function animate() {
    if (state.isPaused) return;

    const now = performance.now();
    const elapsed = now - state.phaseStartTime;
    const progress = Math.min(elapsed / state.phaseDuration, 1);

    // Calculate current scale
    const startScale = state.currentScale;
    const easeProgress = easeInOutCubic(progress);
    const newScale = startScale + (state.targetScale - startScale) * easeProgress;

    // Apply scale
    elements.circle.style.transform = `scale(${newScale})`;
    elements.circle.style.setProperty('--current-scale', newScale);

    if (progress < 1) {
      state.animationFrame = requestAnimationFrame(animate);
    } else {
      // Phase complete
      state.currentScale = state.targetScale;
      nextPhase();
    }
  }

  function nextPhase() {
    const method = METHODS[state.method];
    state.currentPhaseIndex++;

    if (state.currentPhaseIndex >= method.phases.length) {
      // One full breath cycle complete
      state.currentPhaseIndex = 0;
      state.breathCount++;
      updateBreathCount();
      checkLeadCapture();

      // Check if we've completed target rounds
      if (state.targetRounds > 0 && state.breathCount >= state.targetRounds) {
        completeSession();
        return;
      }
    }

    startPhase();
  }

  function completeSession() {
    state.isRunning = false;
    state.isPaused = false;
    cancelAnimationFrame(state.animationFrame);
    clearCountdown();

    elements.app.setAttribute('data-state', 'idle');
    elements.phaseText.textContent = 'Complete';
    elements.countdown.classList.remove('visible');

    // Play completion sound
    if (state.soundEnabled) {
      playSingingBowl(330, true);
      setTimeout(() => playSingingBowl(440, true), 300);
    }

    // Reset after a moment
    setTimeout(() => {
      elements.phaseText.textContent = 'Tap to Start';
      // Exit fullscreen mode
      elements.app.classList.remove('is-fullscreen');
      checkLeadCapture();
    }, 3000);
  }

  function updatePhaseText(phaseType) {
    const texts = {
      'inhale': 'Breathe In',
      'inhale-top': 'Top Up',
      'hold': 'Hold',
      'exhale': 'Breathe Out'
    };
    elements.phaseText.textContent = texts[phaseType] || phaseType;
  }

  function updateBreathCount() {
    const count = state.breathCount;
    elements.breathCount.textContent = `${count} breath${count !== 1 ? 's' : ''}`;
  }

  function showPrompt() {
    state.promptIndex = (state.promptIndex + 1) % PROMPTS.length;
    elements.promptText.textContent = PROMPTS[state.promptIndex];
    elements.promptText.classList.add('visible');
  }

  function hidePrompt() {
    elements.promptText.classList.remove('visible');
  }

  // ==========================================================================
  // Countdown
  // ==========================================================================

  function startCountdown(seconds) {
    // Clear any existing countdown
    clearCountdown();

    state.countdownValue = seconds;
    updateCountdownDisplay();
    elements.countdown.classList.add('visible');

    state.countdownInterval = setInterval(() => {
      if (state.isPaused) return;

      state.countdownValue--;
      if (state.countdownValue > 0) {
        updateCountdownDisplay();
      } else {
        clearCountdown();
      }
    }, 1000);
  }

  function updateCountdownDisplay() {
    if (elements.countdown) {
      elements.countdown.textContent = state.countdownValue;
    }
  }

  function clearCountdown() {
    if (state.countdownInterval) {
      clearInterval(state.countdownInterval);
      state.countdownInterval = null;
    }
    if (elements.countdown) {
      elements.countdown.classList.remove('visible');
      elements.countdown.textContent = '';
    }
  }

  // ==========================================================================
  // Method Selection
  // ==========================================================================

  function setMethod(methodKey) {
    if (!METHODS[methodKey]) return;

    state.method = methodKey;
    elements.methodLabel.textContent = METHODS[methodKey].name;

    // Update active state on cards
    elements.methodCards.forEach(card => {
      card.classList.toggle('active', card.dataset.method === methodKey);
    });

    // Save preference
    localStorage.setItem(STORAGE_KEYS.preferredMethod, methodKey);

    // If running, restart with new method
    if (state.isRunning && !state.isPaused) {
      state.currentPhaseIndex = 0;
      cancelAnimationFrame(state.animationFrame);
      startPhase();
    }
  }

  function loadPreferredMethod() {
    const saved = localStorage.getItem(STORAGE_KEYS.preferredMethod);
    if (saved && METHODS[saved]) {
      setMethod(saved);
    }
  }

  // ==========================================================================
  // Modals
  // ==========================================================================

  function openModal(modal) {
    modal.setAttribute('aria-hidden', 'false');
    // Pause breathing if running
    if (state.isRunning && !state.isPaused) {
      togglePause();
    }
  }

  function closeModal(modal) {
    modal.setAttribute('aria-hidden', 'true');
  }

  function openMethodModal() {
    openModal(elements.methodModal);
  }

  function openLeadModal() {
    elements.completedBreaths.textContent = state.breathCount;
    openModal(elements.leadModal);
    state.hasShownLeadCapture = true;
  }

  // ==========================================================================
  // Lead Capture
  // ==========================================================================

  function checkLeadCapture() {
    // Don't show if already captured or dismissed too many times
    if (localStorage.getItem(STORAGE_KEYS.leadCaptured)) return;

    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.dismissCount) || '0');
    if (dismissCount >= MAX_DISMISS_COUNT) return;

    // Show after threshold breaths
    if (state.breathCount >= LEAD_CAPTURE_THRESHOLD && !state.hasShownLeadCapture) {
      // Wait for current phase to feel natural
      setTimeout(() => {
        if (state.isRunning) {
          openLeadModal();
        }
      }, 1000);
    }
  }

  function handleLeadSubmit(e) {
    e.preventDefault();

    const email = elements.emailInput.value.trim();
    if (!email) return;

    const submitBtn = elements.leadForm.querySelector('.breathing-app__submit-btn');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    // Get Klaviyo list ID from app data attributes
    const listId = elements.app.dataset.klaviyoListId;

    if (listId) {
      // Klaviyo subscription
      fetch(`https://a.klaviyo.com/api/v2/list/${listId}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: window.klaviyoPublicKey || '',
          profiles: [{
            email: email,
            source: 'Breathing App',
            $consent: ['email'],
            breathing_method: state.method,
            breaths_completed: state.breathCount
          }]
        })
      }).catch(() => {
        // Silently fail - we'll still mark as captured
      });
    }

    // Mark as captured regardless of API success
    localStorage.setItem(STORAGE_KEYS.leadCaptured, 'true');

    submitBtn.textContent = 'Thank you!';
    setTimeout(() => {
      closeModal(elements.leadModal);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Get Free Guide';
    }, 1500);
  }

  function handleLeadDismiss() {
    const current = parseInt(localStorage.getItem(STORAGE_KEYS.dismissCount) || '0');
    localStorage.setItem(STORAGE_KEYS.dismissCount, String(current + 1));
    closeModal(elements.leadModal);
  }

  // ==========================================================================
  // Sound Toggle
  // ==========================================================================

  function toggleSound() {
    state.soundEnabled = !state.soundEnabled;
    elements.soundBtn.setAttribute('data-muted', !state.soundEnabled);
    localStorage.setItem(STORAGE_KEYS.soundEnabled, state.soundEnabled);

    // Initialize audio context on first enable (requires user gesture)
    if (state.soundEnabled && !audioContext) {
      initAudio();
    }

    // Play test chime
    if (state.soundEnabled) {
      playChime(523.25, 0.1);
    }
  }

  function loadSoundPreference() {
    const saved = localStorage.getItem(STORAGE_KEYS.soundEnabled);
    state.soundEnabled = saved === 'true';
    elements.soundBtn.setAttribute('data-muted', !state.soundEnabled);
  }

  // ==========================================================================
  // Event Handlers
  // ==========================================================================

  function handleCircleClick(e) {
    // Ignore if clicking on settings or other buttons
    if (e.target.closest('button') || e.target.closest('a')) return;

    if (!state.isRunning) {
      startBreathing();
    } else {
      togglePause();
    }
  }

  function handleMethodCardClick(e) {
    const card = e.target.closest('[data-method]');
    if (card) {
      setMethod(card.dataset.method);
      closeModal(elements.methodModal);
    }
  }

  function handleModalClose(e) {
    const closeBtn = e.target.closest('[data-close-modal]');
    if (closeBtn) {
      const modal = closeBtn.closest('.breathing-app__modal');
      if (modal) {
        // Check if this is the lead modal dismiss
        if (modal.id === 'leadModal' && closeBtn.classList.contains('breathing-app__dismiss-btn')) {
          handleLeadDismiss();
        } else {
          closeModal(modal);
        }
      }
    }
  }

  function handleKeydown(e) {
    // Space to start/pause
    if (e.code === 'Space' && !e.target.matches('input, textarea')) {
      e.preventDefault();
      handleCircleClick({ target: elements.circle });
    }

    // Escape to close modals
    if (e.code === 'Escape') {
      const openModal = document.querySelector('.breathing-app__modal[aria-hidden="false"]');
      if (openModal) {
        if (openModal.id === 'leadModal') {
          handleLeadDismiss();
        } else {
          closeModal(openModal);
        }
      }
    }
  }

  // Handle visibility change (phone sleep)
  function handleVisibilityChange() {
    if (document.hidden && state.isRunning && !state.isPaused) {
      togglePause();
    }
  }

  // ==========================================================================
  // Utilities
  // ==========================================================================

  function easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function debounce(fn, delay) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  function init() {
    initElements();
    initVideo();
    loadPreferredMethod();
    loadSoundPreference();

    // Event listeners
    elements.circle.closest('.breathing-app__circle-container').addEventListener('click', handleCircleClick);
    elements.modeBtn.addEventListener('click', openMethodModal);
    elements.soundBtn.addEventListener('click', toggleSound);
    elements.leadForm.addEventListener('submit', handleLeadSubmit);

    // Scroll hint - smooth scroll to next section
    if (elements.scrollHint) {
      elements.scrollHint.addEventListener('click', function() {
        const nextSection = elements.app.closest('.shopify-section').nextElementSibling;
        if (nextSection) {
          nextSection.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    // Back button - exit fullscreen and stop breathing
    if (elements.backBtn) {
      elements.backBtn.addEventListener('click', function() {
        if (state.isRunning) {
          // Stop the breathing session
          state.isRunning = false;
          state.isPaused = false;
          cancelAnimationFrame(state.animationFrame);
          clearCountdown();
          elements.app.setAttribute('data-state', 'idle');
          elements.phaseText.textContent = 'Tap to Start';
          elements.circle.style.transform = 'scale(1)';
          state.currentScale = 1;
        }
        // Exit fullscreen mode
        elements.app.classList.remove('is-fullscreen');
      });
    }

    // Method selection
    elements.methodCards.forEach(card => {
      card.addEventListener('click', handleMethodCardClick);
    });

    // Round selection
    elements.roundBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const rounds = parseInt(btn.dataset.rounds);
        state.targetRounds = rounds;
        elements.roundBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Modal close handlers
    document.querySelectorAll('.breathing-app__modal').forEach(modal => {
      modal.addEventListener('click', handleModalClose);
    });

    // Keyboard navigation
    document.addEventListener('keydown', handleKeydown);

    // Visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set initial state
    elements.app.setAttribute('data-state', 'idle');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
